import { CommonModule } from "@angular/common";
import { Component, Injector, NgZone, OnInit, Optional } from "@angular/core";
import { ActivationStart, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Services, Wrapper } from "./components/wrapper.component";
import { filter, interval, switchMap, take, takeUntil } from "rxjs";
import { SwUpdate } from '@angular/service-worker';
import { addScriptToDocumentBySource, sleepForMS } from "./helpers/helpers";
import { userFeature } from "./store/user.era";
import { User } from "../../globals/store/user.data";
import { uiFeature } from "./store/ui.era";
import { JoiEvent, LOAD_ALL_EVENTS_FLAG } from "../../globals/store/events.data";
import { eventsActions, eventsFeature } from "./store/events.era";
import { permissionsActions, permissionsFeature } from "./store/permissions.era";
import { accountsFeature } from "./store/accounts.era";
import { Account } from "../../globals/store/accounts.data";
import { TakeoverLoaderComponent } from "./components/takeover-loader/takeover-loader.component";
import { NavBarComponent } from "./components/nav-bar/nav-bar.component";
import { MenuPanelComponent } from "./components/menu-panel/menu-panel.component";
import { TransitionService } from "./services/transition.service";
import { environment } from "../environments/environment";

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TakeoverLoaderComponent, MenuPanelComponent, NavBarComponent],
  template: `
<router-outlet/> 
@if(!!takeover_loading){
  <takeover-loader [type]="takeover_loading"></takeover-loader>
} @else {
  @if(!hide_nav_bar && loggedIn){
    <nav-bar></nav-bar>
  }
  @if(!mobileView && loggedIn){
    <menu-panel class="split-page-menu" [mobile]="false"></menu-panel>
  }
}

  `,
  styles: [`
    :host { display: flex; height: 100vh; width: 100vw; }
  `],
  styleUrls: ["./app.component.scss"],
  standalone: true
})
export class AppComponent extends Wrapper<AppComponent> implements OnInit {
  loggedIn: boolean;
  takeover_loading: string;
  is_engaged: boolean;
  swUpdate_interval = 10 * 60 * 1000;
  swUpdate_first = 1000;
  update_refresh_prompt_present:Boolean;
  checkedPageType = false
  hide_nav_bar = false;

  event: JoiEvent;
  account: Account | Partial<Account>;
  user: User

  readonly entry_routes = ['/login', '/reset-password', '/auth', '/sign-up', '/sign-in'];
  constructor(
    private injector: Injector,
    private transitionService: TransitionService,
    private ngZone: NgZone,
    @Optional() private swUpdate: SwUpdate,
  ){
    Services.initialize(injector);
    super();

    addScriptToDocumentBySource(`https://maps.googleapis.com/maps/api/js?key=${environment.firebase.apiKey}&libraries=places&loading=async`)  

    this.subProp<boolean, 'loggedIn'>(this.store.select(userFeature.selectLoggedIn), 'loggedIn')
    this.subProp<string, 'takeover_loading'>(this.store.select(uiFeature.selectTakeover_loading), 'takeover_loading')
    this.subProp<boolean, 'is_engaged'>(this.store.select(uiFeature.selectEngage_is_engaged), 'is_engaged')

    this.router.events.pipe(filter(e=> e instanceof NavigationEnd), takeUntil(this.$destroy)).subscribe((event:NavigationEnd)=>{
      const url = event.urlAfterRedirects;
      console.log('Navigated to ', {url});
    })

    this.subCallback(this.router.events, async(e) => {
      if (e instanceof ActivationStart) {
        if(e.snapshot.children.length > 0) return;
        let params = {};
        if(e.snapshot.outlet === 'primary'){
            params = e.snapshot.params
        }else{
            e.snapshot.parent.children.forEach(snap=>{
                if(snap.outlet === 'primary') params = snap.params;
            })
        }
          console.log({ outlet: e.snapshot.outlet})
        if(e.snapshot.outlet !== 'engage'){
          let load_eventId = LOAD_ALL_EVENTS_FLAG;
          if(params.hasOwnProperty('eventId')){
            load_eventId = params['eventId'];
          }
          console.log({load_eventId, outlet: e.snapshot.outlet})
          const eventId = await this.take1(this.store.select(eventsFeature.selectEventId))
          if(eventId !== load_eventId) {
            this.store.dispatch(eventsActions.selectEventId( load_eventId ))
          }

        }
        // if(e.snapshot.data && e.snapshot.data.traceName){
        //   const traceName = e.snapshot.data.traceName;
        //   // console.log("Calling to perf to start " + traceName)
        //   this.performanceService.start(traceName);
        // }
        // if (e.snapshot.routeConfig.outlet === 'engage') {
        //   const engageId = e.snapshot.params.id
        //   const traceName = engageRouteToTraceName[engageId]
        //   if (!traceName) {
        //     console.log(`No trace for engageId [${engageId}]`)
        //   } else {
        //     this.performanceService.start(traceName)
        //     console.log(`Engage traceName: ${traceName}`)
        //   }
        // }
      // console.log({ e});
      } else if(e instanceof NavigationEnd){
        this.hide_nav_bar = this.entry_routes.reduce((acc, cur) => acc || RegExp(cur).test(e.url), false);
        if(/\/event\/dashboard\//.test(e.url)) this.store.dispatch(permissionsActions.setPageType('event dashboard'))
        if(/\/account\//.test(e.url)) this.store.dispatch(permissionsActions.setPageType('account dashboard'))
        if (!this.checkedPageType) {
          const groups = await this.take1(this.store.select(permissionsFeature.selectGroups))
          this.checkedPageType = true
          const matchUrl = e.url.match(/\/(event\/[a-zA-Z_-]+)\//)
          if (!matchUrl || !matchUrl[1]) return
          console.log({groups})
          if(!groups || !groups.length) {
            console.log("No groups found in store, cannot set page type")
            return
          }
          const selected_group = groups.find(group=>!!group.pages.find(page => e.url.indexOf(page.url) > -1))
          if (selected_group) this.store.dispatch(permissionsActions.setPageType(selected_group.handle))
        }          
      }
    })
  }
  ngOnInit() {
    // Initialization logic here

    // no need to use destroy for top level app component
    this.subCallback(this.store.select(userFeature.selectUser), user => {
      this.user = user
    })
    this.subProp(this.store.select(eventsFeature.selectEvent), 'event')
    this.subProp(this.store.select(accountsFeature.selectAccount), 'account')

    // joireloader is used to keep the latest version pulled from service workers
    const joireloader = document.getElementById('joireloader');
    if(joireloader){
      joireloader.classList.add('unneeded');
      if(window['joireloader_timeout']) clearTimeout(window['joireloader_timeout'])
      if(window['joireloader_timer']) clearInterval(window['joireloader_timer'])
      window['joireloader_unneeded'] = true;
    }

    if(this.swUpdate && this.swUpdate.isEnabled){
      this.swUpdate.checkForUpdate().then(() => {
        this.swUpdate.activateUpdate().then(() => {
          if(this.update_refresh_prompt_present) return;
          this.update_refresh_prompt_present = true;
          console.log('there is a new version of the app available');
          // this.store.dispatch(new AddUiMessage({
          //   code:this.uiService.FRESH_VERSION,
          //   type:'message',
          //   message:'There is a newer version of Joi.',
          //   button_text:'Click to refresh',
          //   action:'critical'
          // }))
        })
      })
      this.ngZone.runOutsideAngular(() => {
        const init = interval(this.swUpdate_first).pipe(take(1));
        const check = init.pipe(switchMap(()=>interval(this.swUpdate_interval)))

        check.subscribe(() => {
          this.swUpdate_first = this.swUpdate_interval;
          this.ngZone.run(() => { this.swUpdate.checkForUpdate() });
        });
      });
    }
  }
}